'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { addProductToFavorites } from '@eliadmin/core/src/api/customers/customers-api';
import { fetchAllFavorites } from '@eliadmin/core/src/components/dlz/account/favorites/favorites';
import { FAVORITES_COPY } from '@eliadmin/core/src/components/dlz/account/favorites/favorites-copy';
import { DialogCard } from '@eliadmin/core/src/components/dlz/dialogs/dialog-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * The API has only the favourite toggle (`set-favorite`), so emptying the list toggles every
 * favourite of a freshly read list – a stale list could toggle a removed product back on.
 */
function useRemoveAllFavorites() {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!authToken) return;
      const favorites = await fetchAllFavorites({ locale, authToken });
      let failed = 0;
      for (const product of favorites) {
        await addProductToFavorites({ locale, authToken, productSlug: product.slug }).catch(
          () => failed++,
        );
      }
      if (failed) throw new Error(`${failed} favourites were not removed`);
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [CacheKey.FAVORITE_PRODUCTS] }),
        // Cards elsewhere read `is_favorite` from the product detail cache.
        queryClient.invalidateQueries({ queryKey: [CacheKey.PRODUCT_DETAIL] }),
      ]),
  });
}

/**
 * Figma „Odstrániť všetky obľúbené“ (60459:92830, phones only): DLZ/Body/S Medium link in
 * text/brand under the cart button. It is destructive, so it asks first in the DLZ dialog card.
 */
export function RemoveAllFavorites({ className }: { className?: string }) {
  const { t } = useTranslations();
  const [open, setOpen] = useState(false);
  const removal = useRemoveAllFavorites();
  const cancel = useRef<HTMLButtonElement>(null);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={next => {
        if (removal.isPending) return;
        setOpen(next);
        if (!next) removal.reset();
      }}
    >
      <Dialog.Trigger
        className={cn(
          'dlz-body-s-medium rounded-sm text-[#D7191F] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]',
          className,
        )}
      >
        {FAVORITES_COPY.removeAll}
      </Dialog.Trigger>
      <DialogCard
        title={FAVORITES_COPY.removeDialog.title}
        description={FAVORITES_COPY.removeDialog.description}
        className="max-w-[539px]"
        // A destructive question opens on the safe answer.
        onOpenAutoFocus={event => {
          event.preventDefault();
          cancel.current?.focus();
        }}
      >
        {removal.isError ? (
          <p role="alert" className="dlz-body-s text-[#D7191F]">
            {FAVORITES_COPY.removeDialog.failed}
          </p>
        ) : null}
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button
            isLoading={removal.isPending}
            className="h-12 md:h-[52px]"
            onClick={() => removal.mutate(undefined, { onSuccess: () => setOpen(false) })}
          >
            {t('text1519')}
          </Button>
          <Dialog.Close asChild>
            <Button
              ref={cancel}
              variant="secondary"
              disabled={removal.isPending}
              className="h-12 md:h-[52px]"
            >
              {t('text377')}
            </Button>
          </Dialog.Close>
        </div>
      </DialogCard>
    </Dialog.Root>
  );
}
